// Sandboxes persistence for a playtest launch, and nothing else.
//
// MUST be the first import in main.ts, and imports nothing itself.
//
// The reason is module evaluation order. Half a dozen modules here read
// localStorage at *import* time -- learnedRunes.ts hydrates its set, settings.ts
// loads Developer Mode, tutorialHints.ts loads its seen-flags -- so by the time
// any function runs, the real save has already been read, and the next write puts
// a playtest's state where the player's belongs. A launch from the editor would
// quietly cost someone their learned runes, their notebook and their save slot.
//
// Swapping the whole Storage object once, before any of that happens, is what
// makes the guarantee total: no module needs to know it is in a playtest, no
// future module can forget to check, and there is no list of keys to keep in sync
// with the ones that matter. Seeded from the real storage so a playtest still
// opens with your keybindings and sprite, then writes nowhere.
//
// Not gated on Developer Mode: this has to be safe before any setting is read.
// The hash flag is spelled out rather than imported from sim/playtest.ts, which is
// the one exception to that file being the shared vocabulary: this module imports
// nothing, so that being first in main.ts is a guarantee rather than a hope.
function isPlaytestLaunch(): boolean {
  try {
    return new URLSearchParams(location.hash.replace(/^#/, '')).has('playtest');
  } catch {
    return false;
  }
}

function memoryStorage(seed: Storage): Storage {
  const map = new Map<string, string>();
  try {
    for (let i = 0; i < seed.length; i++) {
      const key = seed.key(i);
      if (key !== null) map.set(key, seed.getItem(key) ?? '');
    }
  } catch {
    // An unreadable real storage is not a reason to fail to launch.
  }
  return {
    get length() {
      return map.size;
    },
    key: (i: number) => [...map.keys()][i] ?? null,
    getItem: (key: string) => map.get(key) ?? null,
    setItem: (key: string, value: string) => void map.set(key, String(value)),
    removeItem: (key: string) => void map.delete(key),
    clear: () => map.clear(),
  } as Storage;
}

export const IS_PLAYTEST = isPlaytestLaunch();

if (IS_PLAYTEST) {
  Object.defineProperty(window, 'localStorage', { value: memoryStorage(window.localStorage), configurable: true });
}
